import type { SeoControllerData } from '@application/components';
import { WEBSITE_DOMAIN } from '@application/constants';
import type { Country, Language } from '@application/types';
import type { ProSearchData } from '@application/utils';
import { addTrailingSlash, encodeUri, useRouter } from '@application/utils';
import { ProfessionalTypeEnum } from '@gql/graphql';
import { useMemo } from 'react';

// TODO:  CLEAN THIS UP

interface UseSeoParams {
  proType: ProfessionalTypeEnum;
  proSearch: ProSearchData;
  locationCountry?: string | null;
}

export const useSeo = (params: UseSeoParams) => {
  const { proType, proSearch, locationCountry } = params;
  const { translate, countryCode, language, infoFromCurrentPath, translatedRoute } = useRouter();

  const address = proSearch.params.location;

  const headerTitle = useMemo((): string | undefined => {
    const service = proSearch.serviceTitle;
    if (service && address) {
      return translate('proList.title.servicesLocations')
        ?.replace?.('$service', service)
        ?.replace?.('$city', address);
    }

    if (address) {
      let label = 'vet';
      if (proType === ProfessionalTypeEnum.GROOMER) label = 'groomer';
      else if (proType === ProfessionalTypeEnum.EDUCATOR) label = 'educator';
      return translate(`proList.title.${label}.location`).replace('$city', address);
    }

    if (service) {
      return translate('proList.title.services')?.replace?.('$service', service);
    }

    return translate(`proList.title.${proType.toLowerCase()}.default`);
  }, [address, proSearch.serviceTitle, proType, translate]);

  const seoData = useMemo((): SeoControllerData => {
    const service = proSearch.serviceTitle;
    let label = 'vet';
    if (proType === ProfessionalTypeEnum.GROOMER) label = 'groomer';
    else if (proType === ProfessionalTypeEnum.EDUCATOR) label = 'educator';
    const translation = translate(`proList.seo.${label}`, { returnObjects: true }) as any;

    if (service && address) {
      return {
        title: translation.locationAndService.title
          ?.replace?.('$service', service)
          ?.replace?.('$location', address),
        description: translation.locationAndService.description
          ?.replace?.('$service', service)
          ?.replace?.('$location', address),
        keywords: translation.locationAndService.keywords
          ?.replace?.('$service', service)
          ?.replace?.('$location', address),
      };
    }

    if (address) {
      return {
        title: translation.location.title?.replace?.('$location', address),
        description: translation.location.description?.replace?.('$location', address),
        keywords: translation.location.keywords?.replace?.('$location', address),
      };
    }

    if (service) {
      return {
        title: translation.service.title?.replace?.('$service', service),
        description: translation.service.description?.replace?.('$service', service),
        keywords: translation.service.keywords?.replace?.('$service', service),
      };
    }

    return {
      ...translation.general,
    };
  }, [address, proSearch.serviceTitle, proType, translate]);

  const canonicalUrl = useMemo(() => {
    if (
      locationCountry &&
      locationCountry !== countryCode &&
      ['DE', 'FR', 'NL', 'BE', 'CH'].includes(locationCountry)
    ) {
      let newLang: Language = 'fr';
      let newCountry: Country = 'be';
      switch (locationCountry) {
        case 'DE':
          newLang = 'de';
          newCountry = 'de';
          break;
        case 'NL':
          newLang = 'nl';
          newCountry = 'nl';
          break;
        case 'FR':
          newLang = 'fr';
          newCountry = 'fr';
          break;
        case 'CH':
          newLang = ['fr', 'de'].includes(language) ? language : 'fr';
          newCountry = 'ch';
          break;
        default:
          newLang = ['fr', 'be'].includes(language) ? language : 'fr';
          newCountry = 'be';
      }
      const route =
        proType === 'VET'
          ? translatedRoute('vetList', newLang, newCountry)
          : translatedRoute('groomerList', newLang, newCountry);
      return addTrailingSlash(
        encodeUri(`${WEBSITE_DOMAIN}${route}${infoFromCurrentPath.params?.join('/') ?? ''}`),
      );
    }
  }, [
    locationCountry,
    countryCode,
    proType,
    translatedRoute,
    infoFromCurrentPath.params,
    language,
  ]);

  return {
    headerTitle,
    seoData,
    canonicalUrl,
  };
};
